import React, { useState, useRef } from 'react';
import moment from 'moment';
import Modal from 'react-modal';
import Alert from '@components/Widgets/Alert/Alert.react';
import { getParticipantsInfo, getPrimaryMeal, dateCompare, normalizeParticipantsToArray, getParticipantsForSpecificMeal } from '@utils/Meals';
import { isSunbasketFood } from '@utils/Sunbasket';
import { calculateTotalServingsForParticipants, getBaseServings } from '@utils/servingsCalculations';
import {
    buildConsumerLeftoverSlots,
    isParticipantChecked,
    areAllParticipantsSelected,
    toggleParticipantSelection,
    toggleAllParticipants,
    updateGuestCount,
    getGuestCount,
    findConflictingMeals
} from '@utils/leftoverUtils';
import './LeftoverDatesModal.scss';

const LeftoverDatesModal = ({ user, meals, recipes, foods, plans, leftoverParams, ctaTitle = 'Save', closeModal, onSelectLeftoverOffsets, rescheduleMealParams, rescheduleMeals, returnMode = false, onReturnLeftoverData, onShowConflictModal }) => {
    const { leftovers, meal, extraServings: mealExtraServings } = leftoverParams;

    // Build available slots starting from next meal slot after main meal
    const availableSlots = buildConsumerLeftoverSlots(meal, plans, user, rescheduleMealParams);

    const participantDetails = getParticipantsInfo(user);

    let oldLeftovers = meals.filter((m) => m.parent_uuid == meal.uuid).sort(dateCompare);

    let defaultSelected = oldLeftovers.map((m) => {
        const mealWithArrayParticipants = {
            ...m,
            participants: normalizeParticipantsToArray(m.participants)
        };

        return {
            date: m.date,
            mealType: m.meal,
            participants: getParticipantsForSpecificMeal(mealWithArrayParticipants, user)
        };
    });

    if (leftovers) {
        oldLeftovers = leftovers;
        defaultSelected = oldLeftovers.map((m, index) => ({
            date: moment(m.leftoverDate).format('YYYY-MM-DD'),
            mealType: m.mealType,
            participants: m.participants
        }));
    }

    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(() => defaultSelected.length && !rescheduleMealParams ? defaultSelected : []);
    const [extraServings, setExtraServings] = useState((mealExtraServings || 0));

    const getScalingMap = () => {
        const dishServings  = getTotalServings();
        let scalingMap = {};

        dishServings.forEach(({uuid, scaling}) => {
            scalingMap[uuid] = scaling;
        });

        return scalingMap;
    }

    const getDishes = () => {
        let contents = {};

        const mealDishes = meals.filter(m => m?.meal === meal.meal && m?.date === meal.date && !m?.deleted && (recipes[m?.recipe_uuid]?.servings || isSunbasketFood(foods[m?.food_uuid])));

        mealDishes.forEach((dish) => {
            if(recipes[dish.recipe_uuid]?.servings) {
                contents[dish.recipe_uuid] = recipes[dish.recipe_uuid]
            }

            if(foods[dish.food_uuid]?.servings) {
                contents[dish.food_uuid] = foods[dish.food_uuid]
            }
        });

        const dishes= [];
        const addedUuids = new Set(); // Track which dishes we've already added to avoid duplicates

        // First, add dishes from database meals
        mealDishes.forEach(meal => {
            let dish = Object.assign({}, meal);
            if (dish.meal_type !== 'leftover') {
                const leftovers = meals.filter(item => item.parent_uuid === dish.uuid && !item.deleted);

                dish = { ...dish, main: meal, leftovers: leftovers };
                dishes.push(dish);
                addedUuids.add(dish.uuid || dish.id);
            }
        });

        // Then, add dishes from modifiedDishes (from EditMealServingsModalPrimary in return mode)
        if (leftoverParams?.modifiedDishes && Array.isArray(leftoverParams.modifiedDishes)) {
            leftoverParams.modifiedDishes.forEach(modifiedDish => {
                const mainMeal = modifiedDish.main;
                if (mainMeal && mainMeal.meal_type !== 'leftover') {
                    const dishId = mainMeal.uuid || mainMeal.id;
                    // Only add if not already in dishes array
                    if (!addedUuids.has(dishId)) {
                        // Add to contents for recipe/food lookup
                        if (mainMeal.recipe_uuid && recipes[mainMeal.recipe_uuid]?.servings) {
                            contents[mainMeal.recipe_uuid] = recipes[mainMeal.recipe_uuid];
                        }
                        if (mainMeal.food_uuid && foods[mainMeal.food_uuid]?.servings) {
                            contents[mainMeal.food_uuid] = foods[mainMeal.food_uuid];
                        }

                        // Add the dish with its leftovers
                        dishes.push({
                            main: mainMeal,
                            leftovers: modifiedDish.leftovers || []
                        });
                        addedUuids.add(dishId);
                    }
                }
            });
        }

        return { dishes, contents };
    }

    const getTotalServings = () => {
        const {dishes, contents} = getDishes();

        return dishes.map(({main: dish}) => {
            const dishContent = contents[dish.recipe_uuid || dish.food_uuid];

            // Get all participants: main meal + all selected leftovers
            const mainMealParticipants = getParticipantsForSpecificMeal(dish, user);
            const leftoverParticipants = selected.flatMap(selection => selection.participants);
            const allParticipants = [...mainMealParticipants, ...leftoverParticipants];

            // Use utility to calculate total servings (handles USER logged servings)
            const totalServings = calculateTotalServingsForParticipants(allParticipants, dish, dishContent);

            const baseServings = getBaseServings(dishContent);
            const scaling = totalServings / baseServings;
            const totalYield = totalServings ? (scaling || 1) * baseServings : 0;
            const uuid = dish.uuid;
            const title = contents[dish.recipe_uuid]?.title || contents[dish.food_uuid]?.name;

            return {uuid, title, totalYield, totalServings, totalLeftoverServings: totalServings, scaling};
        });
    }

    const isChecked = (date, mealTypeName, participantUuid) => {
        return isParticipantChecked(selected, 'date', date, mealTypeName, participantUuid);
    }

    const allParticipantsSelected = (date, mealTypeName) => {
        const match = selected.find(item => item.date === date && item.mealType === mealTypeName);
        return areAllParticipantsSelected(match, participantDetails);
    }

    const toggleCheckbox = (date, mealTypeName, participantUuid) => {
        setSelected(prev => toggleParticipantSelection(prev, 'date', date, mealTypeName, participantUuid, participantDetails));
    };

    const selectAllParticipants = (date, mealTypeName) => {
        setSelected(prev => toggleAllParticipants(prev, 'date', date, mealTypeName, participantDetails));
    }

    const onChangeNumberOfGuests = (date, mealTypeName, newGuestCount) => {
        setSelected(prev => updateGuestCount(prev, 'date', date, mealTypeName, newGuestCount, 'Guest '));
    };

    const onChangeParticipantPortion = (date, mealTypeName, participantUuid, newPortion) => {
        setSelected(prev => {
            const match = prev.find(item => item.date === date && item.mealType === mealTypeName);
            const selectedWithoutMatch = prev.filter(item => !(item.date === date && item.mealType === mealTypeName));

            // If participant is not checked, check them first
            if (!match) {
                const addParticipant = participantDetails.find((p) => p.uuid === participantUuid);
                const newParticipant = { ...addParticipant, portion: Math.max(0.25, newPortion) };
                return [...prev, { date, mealType: mealTypeName, participants: [newParticipant] }];
            }

            // Update the portion
            const updatedParticipants = match.participants.map(p => {
                if (p.uuid === participantUuid) {
                    return { ...p, portion: Math.max(0.25, newPortion) };
                }
                return p;
            });

            return [...selectedWithoutMatch, { date, mealType: mealTypeName, participants: updatedParticipants }];
        });
    };

    const getCurrentGuestCount = (date, mealTypeName) => {
        return getGuestCount(selected, 'date', date, mealTypeName);
    };

    const clearAll = () => {
        setSelected([]);
        setExtraServings(0);
    }

    const onClickCta = () => {
        if(rescheduleMealParams) {
            const userLeftovers = selected.map(({date, mealType, participants}) => {
                return {leftoverDate: moment(date, 'YYYY-MM-DD'), mealType, participants};
            });
            rescheduleMeals(rescheduleMealParams.meals, rescheduleMealParams.date, rescheduleMealParams.mealType, userLeftovers);
        } else if (returnMode && onReturnLeftoverData) {
            // Return mode: send data back to parent instead of applying directly
            const leftovers = selected.map(({date, mealType, participants}) => {
                // Check if this leftover already exists with same date and mealType
                // If only participants changed, it's not a conflict
                const isExistingLeftover = oldLeftovers.some(oldLeftover => {
                    if (oldLeftover.date && oldLeftover.meal) {
                        return oldLeftover.date === date && oldLeftover.meal === mealType;
                    }
                    if (oldLeftover.leftoverDate && oldLeftover.mealType) {
                        const oldDate = moment(oldLeftover.leftoverDate).format('YYYY-MM-DD');
                        return oldDate === date && oldLeftover.mealType === mealType;
                    }
                    return false;
                });

                // Find conflicting meals only if this is a NEW leftover (date/mealType changed)
                const conflictingMeals = isExistingLeftover
                    ? []
                    : findConflictingMeals(date, mealType, [], meals, 'date', 'meal');

                return {leftoverDate: moment(date, 'YYYY-MM-DD'), mealType, participants, conflictingMeals};
            });

            const scalingMap = getScalingMap();
            const hasConflicts = leftovers.some(leftover => leftover.conflictingMeals.length > 0);

            // Show conflict modal if conflicts exist
            if (hasConflicts && onShowConflictModal) {
                onShowConflictModal(leftovers, scalingMap, extraServings);
            } else {
                onReturnLeftoverData(leftovers, scalingMap, extraServings);
            }
            closeModal();
        } else {
            // Non-return mode (swap meal flow): check for conflicts before applying
            const leftovers = selected.map(({date, mealType, participants}) => {
                // Note: No need to filter out main meal/existing leftovers since this is for adding/swapping
                const conflictingMeals = findConflictingMeals(date, mealType, [], meals, 'date', 'meal');
                return {leftoverDate: moment(date, 'YYYY-MM-DD'), mealType, participants, conflictingMeals};
            });
            const scalingMap = getScalingMap();
            const hasConflicts = leftovers.some(leftover => leftover.conflictingMeals.length > 0);

            if (hasConflicts && onShowConflictModal) {
                onShowConflictModal(leftovers, scalingMap, extraServings);
                closeModal();
            } else {
                onSelectLeftoverOffsets(leftovers, scalingMap, extraServings);
            }
        }
    }

    return (
        <Modal isOpen={true}
            onRequestClose={closeModal}
            closeModal={closeModal}
            contentLabel="Overwrite with leftovers"
            className="el-modal el-modal2 el-modal2-centered leftovers-modal"
            overlayClassName="el-modal-overlay"
            closeTimeoutMS={250}>
            <section className="el-modal-container el-modal2-container leftovers-modal-container">
                <header>
                    <h2>Leftovers</h2>
                    <button className="el-modal-close-x" onClick={closeModal}>
                        <i className="icon-close-x" />
                        <span className="assistive-text">Close Modal</span>
                    </button>
                </header>

                <div className="el-modal-body-container el-modal2-body-container el-fonts leftovers-modal-body">
                    {error ? <Alert type="error" description={error} /> : null}

                    <div className="sub-header">
                        <div className="sub-header-title">When would you like to use your leftovers?</div>

                        <div className="sub-header-controls">
                            {/* Store for Later Section */}
                            <div className="store-for-later-section">
                                <div className="store-for-later-label">Store extras for later (servings per dish)</div>
                                <div className="store-for-later-controls">
                                    <button
                                        className="store-for-later-btn"
                                        onClick={() => setExtraServings(Math.max(0, extraServings - 1))}
                                        disabled={extraServings <= 0}
                                    >
                                        -
                                    </button>
                                    <span className="store-for-later-count">{extraServings}</span>
                                    <button
                                        className="store-for-later-btn"
                                        onClick={() => setExtraServings(extraServings + 1)}
                                    >
                                        +
                                    </button>
                                </div>
                            </div>

                            <button className="clear-selections" onClick={() => clearAll()}>clear all</button>
                        </div>
                    </div>

                    <div className="leftovers-container">
                        {availableSlots.map((slot, slotIndex) => {
                            const { date, mealTypes } = slot;
                            let dateFormat = moment(date).format('ddd, MMM Do');

                            if (moment().subtract(1, 'day').isSame(date, 'day')) {
                                dateFormat = 'Yesterday';
                            } else if (moment().isSame(date, 'day')) {
                                dateFormat = 'Today';
                            } else if (moment().add(1, 'day').isSame(date, 'day')) {
                                dateFormat = 'Tomorrow';
                            }

                            return (
                                <div key={`${date}-${slotIndex}`} className="leftover-date">
                                    <span className="date">{dateFormat}</span>
                                    {mealTypes.map((mealType) => {
                                        const mealTypeDateMeals = meals.filter((m) => m.date === date && m.meal === mealType.name);
                                        const { titles } = getPrimaryMeal(mealTypeDateMeals, recipes, foods);
                                        const hasScheduledMeal = titles.length > 0;
                                        
                                        return (
                                            <div key={`${dateFormat}_${mealType.name}`} className="meal-type-container">
                                                <div className="meal-type-header">
                                                    <button
                                                        className="el-checkbox meal-type-name"
                                                        data-active={allParticipantsSelected(date, mealType.name)}
                                                        onClick={() => selectAllParticipants(date, mealType.name)}
                                                    >
                                                        {mealType.name}
                                                    </button>
                                                    {hasScheduledMeal ? (
                                                        <span className="meal-name">Currently scheduled: {titles.join(' + ')}</span>
                                                    ) : null}
                                                </div>
                                                <div className="meal-type-participants">
                                                    {participantDetails.map((participant, participantIndex) => {
                                                        const participantIsChecked = isChecked(date, mealType.name, participant.uuid);

                                                        return (
                                                            <div key={`${dateFormat}_${mealType.name}_${participant.name}`} className="participant-container">
                                                                <button
                                                                    className="el-checkbox"
                                                                    data-active={participantIsChecked}
                                                                    onClick={() => toggleCheckbox(date, mealType.name, participant.uuid)}
                                                                >
                                                                    <span className="participant-name">{participant.name}</span>
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                    
                                                    {/* Guest controls */}
                                                    <div className="participant-container guest-container">
                                                        <div className="guest-info">
                                                            <span className="guest-label">Guests</span>
                                                            <div className="guest-count-controls">
                                                                <button
                                                                    className="guest-btn"
                                                                    disabled={getCurrentGuestCount(date, mealType.name) <= 0}
                                                                    onClick={() => onChangeNumberOfGuests(date, mealType.name, getCurrentGuestCount(date, mealType.name) - 1)}
                                                                >
                                                                    −
                                                                </button>
                                                                <span className="guest-count">{getCurrentGuestCount(date, mealType.name)}</span>
                                                                <button
                                                                    className="guest-btn"
                                                                    onClick={() => onChangeNumberOfGuests(date, mealType.name, getCurrentGuestCount(date, mealType.name) + 1)}
                                                                >
                                                                    +
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <footer>
                    <button className="el-modal-cancel-btn" onClick={closeModal}>Cancel</button>
                    <button className="el-modal-ok-btn" onClick={() => onClickCta()}>{returnMode ? 'Done' : ctaTitle}</button>
                </footer>
            </section>
        </Modal>
    );
}

export default LeftoverDatesModal;